import { format } from 'date-fns';
import { HoursWithMinutesAndSeconds } from '@sdk/utils/dateFromHours';
import { get24hTimeSlots } from '@sdk/components/forms/TimePicker/timePicker.utils';
import { MIDNIGHT_TIME_SLOT } from '@sdk/components/forms/TimePicker/timePicker.consts';

/**
 * Method is used to determine clockOut picker options based on the value of clockIn time.
 * Example: if slots are 10 minutes and clockIn happened at 10:00:00, the next closest slot is 10:10:00.
 *
 * @param slot slot we based the search on (clockIn value)
 * @param intervalInMinutes intervals used in timepicker options
 * @returns
 */
export const findTheClosestNextSlot = (
  slot: HoursWithMinutesAndSeconds,
  intervalInMinutes: number
) => {
  const slots = get24hTimeSlots(intervalInMinutes);
  const slotIndex = slots.findIndex((currentSlot) => slot <= currentSlot);

  if (slotIndex === -1) {
    // always fallback to midnight
    return MIDNIGHT_TIME_SLOT;
  }

  if (slots[slotIndex] === slot) {
    // Slots can be looped (going to next day), so if we are out of range start from the begining.
    return slots[slotIndex + 1] ? slots[slotIndex + 1] : slots[0];
  }

  return slots[slotIndex];
};

/**
 * Method is used to determine clockIn picker options based on the value of scheduled shift start time.
 * Example: if slots are 10 minutes and shift starts at 10:00:00, the closest slot is 10:00:00.
 *
 * @param slot slot we based the search on (clockIn value)
 * @param intervalInMinutes intervals used in timepicker options
 * @returns
 */
export const findTheClosestSlot = (slot: HoursWithMinutesAndSeconds, intervalInMinutes: number) => {
  const slots = get24hTimeSlots(intervalInMinutes);
  const slotIndex = slots.findIndex((currentSlot) => slot <= currentSlot);

  if (slotIndex === -1) {
    // always fallback to midnight
    return MIDNIGHT_TIME_SLOT;
  }

  return slots[slotIndex];
};

export const getDefaultClockInPickerStartSlot = (
  intervalInMinutes: number,
  date: Date = new Date()
) => findTheClosestSlot(format(date, 'HH:mm:ss'), intervalInMinutes);

export const getDefaultClockOutPickerStartSlot = (
  intervalInMinutes: number,
  date: Date = new Date()
) => findTheClosestNextSlot(format(date, 'HH:mm:ss'), intervalInMinutes);
